// src/components/ShopView.js
import React, { useState, useMemo, useCallback } from 'react';
import styles from './ShopView.module.css';
import { ItemData } from '../data/ItemData';
import { getItemImageSrc } from '../utils/getItemImageSrc';

const ShopView = ({ playerData, onBuyItem, onUpgradeBuilding, buyBackList, onBuyBackItem }) => {
  const [activeCategory, setActiveCategory] = useState('toolsAndBuildings');
  const [selectedItem, setSelectedItem] = useState(null);

  // Define consumable limits and get current tracking
  const consumableLimits = {
    'healingPotion': 1,
    'energyPotion': 1,
    'manaPotion': 1,
    'bandage': 3,
    'apple': 5,
    'berries': 5
  };

  const getConsumableTracking = () => {
    const tracking = playerData.dailyConsumablePurchases || {
      healingPotion: 0,
      energyPotion: 0,
      manaPotion: 0,
      bandage: 0,
      apple: 0,
      berries: 0,
      lastResetDay: playerData.day || 1
    };
    
    // Reset if it's a new day
    const currentDay = playerData.day || 1;
    if (tracking.lastResetDay !== currentDay) {
      return {
        healingPotion: 0,
        energyPotion: 0,
        manaPotion: 0,
        bandage: 0,
        apple: 0,
        berries: 0,
        lastResetDay: currentDay
      };
    }
    
    return tracking;
  };

  const consumableTracking = getConsumableTracking();

  const shopItems = useMemo(() => {
    const categories = {
      consumables: [],
      tools: [],
      weapons: [],
      buildings: [],
      materials: [], 
    };
    
    // Map singular to plural forms
    const categoryMap = {
      'consumable': 'consumables',
      'tool': 'tools',
      'weapon': 'weapons',
      'building': 'buildings',
      'material': 'materials'
    };
    
    // Populate categories based on item.shopCategory
    for (const key in ItemData) {
      const item = ItemData[key];
      if (item.shopCategory) {
        // Map singular to plural form
        const categoryKey = categoryMap[item.shopCategory] || item.shopCategory;
        
        // Check if the mapped category exists in our categories object
        if (categories[categoryKey]) {
          categories[categoryKey].push({ ...item, key });
        } else {
          // Fallback to type-based categorization
          if (item.type === 'tool' && categories.tools) categories.tools.push({ ...item, key });
          else if (item.type === 'weapon' && categories.weapons) categories.weapons.push({ ...item, key });
          else if (item.type === 'material' && categories.materials) categories.materials.push({ ...item, key });
          else if (item.type === 'consumable' && categories.consumables) categories.consumables.push({ ...item, key });
          else if (item.type === 'building' && categories.buildings) categories.buildings.push({ ...item, key });
        }
      }
    }
    return categories;
  }, []); 

  const getDisplayedCost = (item) => {
    return item.shopPriceOverride !== undefined ? item.shopPriceOverride : (item.goldValue || 0); // Default to 0 if no goldValue
  };
  
  const canAfford = useCallback((cost) => playerData.gold >= cost, [playerData.gold]);

  const buildingUpgradeConfig = playerData.buildingUpgradeConfig || {};

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const renderShopItem = (item) => {
    const isConsumableWithLimit = item.type === 'consumable' && consumableLimits[item.key];
    const currentPurchases = isConsumableWithLimit ? (consumableTracking[item.key] || 0) : 0;
    const limit = isConsumableWithLimit ? consumableLimits[item.key] : 0;
    const remainingPurchases = isConsumableWithLimit ? Math.max(0, limit - currentPurchases) : 0;
    const isLimitReached = isConsumableWithLimit && currentPurchases >= limit;
    
    return (
      <div 
        key={item.key} 
        className={`${styles.shopItem} ${selectedItem?.key === item.key ? styles.selectedItem : ''} ${(!canAfford(getDisplayedCost(item)) && item.type !== 'building') || isLimitReached ? styles.disabled : ''} ${item.type === 'building' && playerData.buildings[item.key]?.owned ? styles.ownedBuilding : ''}`}
        onClick={() => handleItemClick(item)}
      >
        <img src={getItemImageSrc(item.key)} alt={item.name} className={styles.itemIcon} />
        <div className={styles.itemInfo}>
          <div className={styles.itemName}>{item.name}</div>
          <div className={styles.itemCost}>{getDisplayedCost(item)} G</div>
          {isConsumableWithLimit && (
            <div className={styles.itemLimit}>
              {isLimitReached ? 'Daily limit reached' : `Remaining: ${remainingPurchases}/${limit}`}
            </div>
          )}
        </div>
      </div>
    );
  };

  const buildingUpgradeRenderItems = useMemo(() => {
    return Object.entries(buildingUpgradeConfig).map(([key, upgradeSettings]) => {
        const buildingOwned = playerData.buildings[key]?.owned;
        if (!buildingOwned) return null;

        const currentLevel = playerData.buildings[key]?.upgradeLevel || 0;
        const maxLevel = upgradeSettings.maxLevel;
        const costs = upgradeSettings.costs;
        const nextLevelCost = currentLevel < maxLevel && costs && costs.length > currentLevel + 1 ? costs[currentLevel + 1] : null;
        const buildingName = ItemData[key]?.name || key;

        const upgradeItem = {
          key,
          name: `${buildingName} (Lvl ${currentLevel})`,
          type: 'buildingUpgrade',
          cost: nextLevelCost,
          currentLevel,
          maxLevel,
          description: `Upgrade your ${buildingName} to level ${currentLevel + 1}`,
          isMaxLevel: currentLevel >= maxLevel
        };

        return (
          <div 
            key={key} 
            className={`${styles.shopItem} ${selectedItem?.key === key && selectedItem?.type === 'buildingUpgrade' ? styles.selectedItem : ''} ${(currentLevel >= maxLevel || (nextLevelCost !== null && !canAfford(nextLevelCost))) ? styles.disabled : ''}`}
            onClick={() => handleItemClick(upgradeItem)}
          >
            <img src={getItemImageSrc(key)} alt={buildingName} className={styles.itemIcon} />
            <div className={styles.itemInfo}>
              <div className={styles.itemName}>{buildingName} (Lvl {currentLevel})</div>
              {currentLevel < maxLevel && nextLevelCost !== null ? (
                <div className={styles.itemCost}>Upgrade: {nextLevelCost} G</div>
              ) : (
                <div className={styles.itemCost}>Max Level</div>
              )}
            </div>
          </div>
        );
      }).filter(Boolean);
  }, [playerData.buildings, buildingUpgradeConfig, playerData.gold, selectedItem]);



  const getCategoryItems = () => {
    switch(activeCategory) {
      case 'toolsAndBuildings':
        return [...shopItems.tools, ...shopItems.weapons, ...shopItems.materials];
      case 'buildings':
        return shopItems.buildings;
      case 'consumables':
        return shopItems.consumables;
      case 'upgrades':
        return buildingUpgradeRenderItems;
      case 'buyBack':
        return buyBackList.map((entry, index) => {
          const buyBackItem = {
            key: entry.itemKeyOrId,
            name: entry.name,
            type: 'buyBack',
            cost: entry.originalValue,
            quantity: entry.quantity,
            durability: entry.durability,
            maxDurability: entry.maxDurability,
            isUnique: entry.isUnique,
            description: `Buy back your ${entry.name}`,
            buyBackIndex: index
          };
          
          return (
            <div 
              key={index} 
              className={`${styles.shopItem} ${selectedItem?.buyBackIndex === index ? styles.selectedItem : ''} ${!canAfford(entry.originalValue) ? styles.disabled : ''}`}
              onClick={() => handleItemClick(buyBackItem)}
            >
              <img src={getItemImageSrc(entry.itemKeyOrId, entry.itemData?.img)} alt={entry.name} className={styles.itemIcon} />
              <div className={styles.itemInfo}>
                <div className={styles.itemName}>{entry.name} {entry.isUnique && entry.maxDurability ? `(${entry.durability}/${entry.maxDurability})` : (entry.quantity > 1 ? `(x${entry.quantity})` : '')}</div>
                <div className={styles.itemCost}>{entry.originalValue} G</div>
              </div>
            </div>
          );
        });
      default:
        return [];
    }
  };

  const renderItemDetails = () => {
    if (!selectedItem) {
      return (
        <div className={styles.detailsPlaceholder}>
          <div className={styles.placeholderIcon}>🛍️</div>
          <h3>Select an item to view details</h3>
          <p>Click on any item from the list to see detailed information, stats, and purchasing options.</p>
        </div>
      );
    }

    const cost = selectedItem.type === 'buildingUpgrade' ? selectedItem.cost : getDisplayedCost(selectedItem);
    const isBuilding = selectedItem.type === 'building';
    const isBuildingUpgrade = selectedItem.type === 'buildingUpgrade';
    const isBuyBack = selectedItem.type === 'buyBack';
    const isOwned = isBuilding && playerData.buildings[selectedItem.key]?.owned;

    return (
      <div className={styles.itemDetails}>
        <div className={styles.detailsHeader}>
          <img src={getItemImageSrc(selectedItem.key)} alt={selectedItem.name} className={styles.detailsIcon} />
          <h3 className={styles.detailsTitle}>{selectedItem.name}</h3>
        </div>

        <div className={styles.detailsContent}>
          <div className={styles.detailsSection}>
            <h4>Description</h4>
            <p>{selectedItem.description || 'No description available.'}</p>
          </div>

          {selectedItem.materials && (
            <div className={styles.detailsSection}>
              <h4>Materials Required</h4>
              <div className={styles.materialsList}>
                {Object.entries(selectedItem.materials).map(([matKey, qty]) => (
                  <div key={matKey} className={styles.materialItem}>
                    <img src={getItemImageSrc(matKey)} alt={ItemData[matKey]?.name || matKey} className={styles.materialIcon} />
                    <span>{ItemData[matKey]?.name || matKey} x{qty}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedItem.type === 'weapon' && (
            <div className={styles.detailsSection}>
              <h4>Combat Stats</h4>
              <div className={styles.statsList}>
                {selectedItem.attackPower && <div className={styles.statItem}>⚔️ Attack Power: {selectedItem.attackPower}</div>}
                {selectedItem.durability && <div className={styles.statItem}>🔧 Durability: {selectedItem.durability}</div>}
                {selectedItem.critChance && <div className={styles.statItem}>💥 Crit Chance: {selectedItem.critChance}%</div>}
              </div>
            </div>
          )}

          {selectedItem.type === 'tool' && (
            <div className={styles.detailsSection}>
              <h4>Tool Stats</h4>
              <div className={styles.statsList}>
                {selectedItem.durability && <div className={styles.statItem}>🔧 Durability: {selectedItem.durability}</div>}
                {selectedItem.efficiency && <div className={styles.statItem}>⚡ Efficiency: {selectedItem.efficiency}</div>}
                {selectedItem.gatherBonus && <div className={styles.statItem}>📦 Gather Bonus: +{selectedItem.gatherBonus}</div>}
              </div>
            </div>
          )}

          {selectedItem.type === 'consumable' && (
            <div className={styles.detailsSection}>
              <h4>Effects</h4>
              <div className={styles.statsList}>
                {selectedItem.healAmount && <div className={styles.statItem}>❤️ Heal: {selectedItem.healAmount} HP</div>}
                {selectedItem.manaAmount && <div className={styles.statItem}>🔮 Mana: {selectedItem.manaAmount} MP</div>}
                {selectedItem.energyAmount && <div className={styles.statItem}>⚡ Energy: {selectedItem.energyAmount}</div>}
              </div>
            </div>
          )}

          <div className={styles.detailsSection}>
            <h4>Cost</h4>
            <div className={styles.costDisplay}>
              <span className={styles.costAmount}>{cost} G</span>
              {!canAfford(cost) && <span className={styles.cannotAfford}>Cannot afford</span>}
            </div>
          </div>
        </div>

        <div className={styles.detailsActions}>
          {isBuildingUpgrade ? (
            selectedItem.isMaxLevel ? (
              <div className={styles.maxLevelButton}>MAX LEVEL</div>
            ) : (
              <button 
                onClick={() => onUpgradeBuilding(selectedItem.key)}
                disabled={!canAfford(cost)}
                className={styles.detailsBuyButton}
              >
                Upgrade to Lvl {selectedItem.currentLevel + 1}
              </button>
            )
          ) : isBuyBack ? (
            <button 
              onClick={() => onBuyBackItem(selectedItem.buyBackIndex)}
              disabled={!canAfford(cost)}
              className={styles.detailsBuyButton}
            >
              Buy Back
            </button>
          ) : (
            <button 
              onClick={() => onBuyItem(selectedItem.key)}
              disabled={(!canAfford(cost) && !isBuilding) || isOwned}
              className={styles.detailsBuyButton}
            >
              {isOwned ? 'Already Owned' : 'Buy Item'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const categories = [
    { id: 'toolsAndBuildings', name: 'Tools & Materials', icon: '🔨' },
    { id: 'buildings', name: 'Buildings', icon: '🏢' },
    { id: 'consumables', name: 'Consumables', icon: '🧪' },
    { id: 'upgrades', name: 'Building Upgrades', icon: '⬆️' },
    { id: 'buyBack', name: 'Buy Back', icon: '🔄' }
  ];

  return (
    <div className={styles.shopViewContainer}>
      <div className={styles.shopHeader}>
        <h2>Welcome to the Shop!</h2>
        <div className={styles.playerGold}>Your Gold: {playerData.gold} G</div>
      </div>

      <div className={styles.shopContent}>
        {/* Left Panel - Categories */}
        <div className={styles.leftPanel}>
          <h3 className={styles.categoriesTitle}>Categories</h3>
          <div className={styles.categoryList}>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`${styles.categoryButton} ${activeCategory === category.id ? styles.active : ''}`}
              >
                <span className={styles.categoryIcon}>{category.icon}</span>
                <span className={styles.categoryName}>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Middle Panel - Items */}
        <div className={styles.middlePanel}>
          <div className={styles.itemsHeader}>
            <h3>{categories.find(c => c.id === activeCategory)?.name || 'Items'}</h3>
          </div>
          <div className={styles.itemsContent}>
            {activeCategory === 'upgrades' || activeCategory === 'buyBack' ? (
              <div className={styles.itemGrid}>
                {getCategoryItems().length > 0 ? getCategoryItems() : <p className={styles.emptyMessage}>No items available in this category.</p>}
              </div>
            ) : (
              <div className={styles.itemGrid}>
                {getCategoryItems().length > 0 ? getCategoryItems().map(renderShopItem) : <p className={styles.emptyMessage}>No items available in this category.</p>}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Item Details */}
        <div className={styles.rightPanel}>
          <div className={styles.detailsHeader}>
            <h3>Item Details</h3>
          </div>
          <div className={styles.detailsContent}>
            {renderItemDetails()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopView;